import { useMemo, useState, type CSSProperties } from 'react'
import { Link, useParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Avatar,
  Button,
  DiffViewer,
  EmptyState,
  ErrorBanner,
  FrmIcon,
  Input,
  Pill,
  SearchInput,
  Select,
  Skeleton,
  StatusPill,
  Tabs,
  Textarea,
  WorkbenchPanel,
  useToast,
} from '@frm/ui'
import {
  confirmAgentGroup,
  confirmAgentEngine,
  confirmAgentModel,
  confirmAgentStyle,
  diffAgentEngine,
  diffAgentGroup,
  diffAgentModel,
  diffAgentStyle,
  fetchAgentGroup,
  fetchAgentEngines,
  fetchAgentLogs,
  fetchAgentModels,
  fetchAgentStyles,
  fetchAgentsHealth,
  pullAgent,
  releaseAgent,
  restartAgent,
  type AgentGroupInput,
  type AgentHealth,
  type AgentModelItem,
} from '../api/agents'
import { fetchSessions, type OwnerSessionDto, type SessionStatus } from '../api/sessions'
import { formatAge } from '../shared/format'
import type { DiffLine } from '@frm/ui'

type DetailTab = 'overview' | 'group' | 'logs'
type OverviewEditMode = 'model' | 'engine' | 'style' | null
type MemberDraft = { id: string; owner: boolean }

const CLAUDE_MODEL_LABELS: Record<string, string> = {
  default: 'Default (recommended)',
  opus: 'Opus',
  fable: 'Fable',
  sonnet: 'Sonnet',
  haiku: 'Haiku',
  'claude-opus': 'Opus',
  'claude-opus-4-8': 'Opus',
  'claude-fable': 'Fable',
  'claude-fable-5': 'Fable',
  'claude-sonnet': 'Sonnet',
  'claude-sonnet-5': 'Sonnet',
  'claude-haiku': 'Haiku',
  'claude-haiku-4-5': 'Haiku',
}

const CODEX_MODEL_DESCRIPTIONS: Record<string, string> = {
  'gpt-5.6-sol': 'Latest frontier agentic coding model.',
  'gpt-5.6-terra': 'Balanced agentic coding model for everyday work.',
  'gpt-5.6-luna': 'Fast and affordable agentic coding model.',
  'gpt-5.5': 'Frontier model for complex coding and research.',
  'gpt-5.4': 'Strong model for everyday coding.',
  'gpt-5.4-mini': 'Small, fast, and cost-efficient model for simpler tasks.',
  'gpt-5.3-codex-spark': 'Ultra-fast coding model.',
}

function modelLabelForUi(engine: string, model: string): string {
  if (engine === 'codex' && model === 'gpt-5.6-sol') return 'gpt-5.6-sol (default)'
  if (engine !== 'claude-code') return model
  return CLAUDE_MODEL_LABELS[model] ?? model
}

function fallbackModelItems(engine: string, models: string[], current: string): AgentModelItem[] {
  return models.filter(Boolean).map((model) => ({
    id: model,
    label: modelLabelForUi(engine, model),
    description: engine === 'codex' ? CODEX_MODEL_DESCRIPTIONS[model] ?? '' : '',
    isCurrent: model === current,
    isDefault: engine === 'codex' ? model === 'gpt-5.6-sol' : model === 'default',
  }))
}

function optionLabelFromItems(items: AgentModelItem[], engine: string, model: string): string {
  return items.find((item) => item.id === model)?.label ?? modelLabelForUi(engine, model)
}

export function AgentDetailPage() {
  const params = useParams<{ id: string }>()
  const agentId = params.id ? decodeURIComponent(params.id) : ''
  const [tab, setTab] = useState<DetailTab>('overview')
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const healthQuery = useQuery({
    queryKey: ['agents', 'health'],
    queryFn: fetchAgentsHealth,
    refetchInterval: 30_000,
  })
  const restartMutation = useMutation({
    mutationFn: restartAgent,
    onSuccess: (result) => {
      toast({
        title: result.result === 'restarted' ? 'Агент перезапущен' : 'Рестарт не выполнен',
        description: `${result.agentId}: ${result.message} · ${result.serviceStatus}`,
        tone: result.result === 'restarted' ? 'success' : 'warning',
      })
      void queryClient.invalidateQueries({ queryKey: ['agents', 'health'] })
    },
    onError: (error) => {
      toast({
        title: 'Рестарт не выполнен',
        description: error instanceof Error ? error.message : 'unknown error',
        tone: 'danger',
      })
    },
  })

  const agent = healthQuery.data?.agents.find((item) => item.name === agentId)

  if (healthQuery.isLoading) {
    return (
      <WorkbenchPanel variant="raised">
        <Skeleton width="40%" height={24} />
        <Skeleton height={260} style={{ marginTop: 18 }} />
      </WorkbenchPanel>
    )
  }

  if (healthQuery.isError) {
    return (
      <ErrorBanner
        title="Карточка агента недоступна"
        description={healthQuery.error instanceof Error ? healthQuery.error.message : 'unknown error'}
        action={<Button onClick={() => void healthQuery.refetch()}>Повторить</Button>}
      />
    )
  }

  if (!agent) {
    return (
      <EmptyState
        title="Агент не найден"
        description={`В health-реестре нет записи "${agentId}".`}
        action={<Button onClick={() => void healthQuery.refetch()}>Обновить</Button>}
      />
    )
  }

  return (
    <div style={agentDetailPageStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--frm-text-3)', fontSize: 13 }}>
        <Link to="/" style={smallLinkStyle}>Агенты</Link>
        <span>/</span>
        <Mono style={{ color: 'var(--frm-text)', fontWeight: 800 }}>{agent.name}</Mono>
      </div>

      <div style={heroStyle}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, minWidth: 0 }}>
          <Avatar name={agent.name} shape="square" size="lg" style={{ flexShrink: 0, width: 54, height: 54, borderRadius: 14, background: 'linear-gradient(135deg, var(--frm-accent), var(--frm-accent-2))', color: '#04111D' }} />
          <div style={{ display: 'grid', gap: 8, minWidth: 0 }}>
            <div style={{ fontSize: 24, fontWeight: 800, lineHeight: 1.1, letterSpacing: '-0.01em' }}>{agent.name}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Pill>{agent.role}</Pill>
              <Pill>{agent.engine}</Pill>
              <AgentStatus agent={agent} />
              {agent.telegramUrl ? (
                <a href={agent.telegramUrl} target="_blank" rel="noreferrer" style={telegramPillStyle}>
                  <TelegramIcon />
                  Telegram
                </a>
              ) : null}
            </div>
          </div>
        </div>
        <div style={heroActionsStyle}>
          <Button
            variant="secondary"
            leftIcon={<LogIcon />}
            style={heroActionButtonStyle}
            onClick={() => setTab('logs')}
          >
            Логи
          </Button>
          <Button
            variant="secondary"
            leftIcon={<FrmIcon name="refresh" size={17} />}
            style={heroRestartButtonStyle}
            loading={restartMutation.isPending}
            disabled={restartMutation.isPending}
            onClick={() => restartMutation.mutate(agent.name)}
          >
            Перезапустить
          </Button>
        </div>
      </div>

      <Tabs
        ariaLabel="Разделы карточки агента"
        variant="underline"
        items={[
          { id: 'overview', label: 'Обзор' },
          { id: 'group', label: 'Группа и доступ' },
          { id: 'logs', label: 'Логи' },
        ]}
        onChange={(value) => setTab(value as DetailTab)}
        value={tab}
        style={{ alignSelf: 'start', width: '100%' }}
      />

      {tab === 'overview' && <OverviewTab agent={agent} />}
      {tab === 'group' && <GroupTab agentId={agent.name} />}
      {tab === 'logs' && <LogsTab agentId={agent.name} />}
    </div>
  )
}

function OverviewTab({ agent }: { agent: AgentHealth }) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [editMode, setEditMode] = useState<OverviewEditMode>(null)
  const [model, setModel] = useState(agent.model)
  const [engine, setEngine] = useState(agent.engine)
  const [engineModel, setEngineModel] = useState(agent.model)
  const [style, setStyle] = useState(agent.style)
  const releaseMutation = useMutation({
    mutationFn: releaseAgent,
    onSuccess: (result) => {
      toast({
        title: result.result === 'ok' ? 'Выпуск выполнен' : 'Выпуск не выполнен',
        description: `${result.agentId}: ${result.message}`,
        tone: result.result === 'ok' ? 'success' : 'warning',
      })
      void queryClient.invalidateQueries({ queryKey: ['agents', 'health'] })
    },
    onError: (error) => toast({
      title: 'Выпуск не выполнен',
      description: error instanceof Error ? error.message : 'unknown error',
      tone: 'danger',
    }),
  })
  const pullMutation = useMutation({
    mutationFn: pullAgent,
    onSuccess: (result) => {
      toast({
        title: result.result === 'ok' ? 'Подтягивание выполнено' : 'Подтягивание не выполнено',
        description: `${result.agentId}: ${result.message}`,
        tone: result.result === 'ok' ? 'success' : 'warning',
      })
      void queryClient.invalidateQueries({ queryKey: ['agents', 'health'] })
    },
    onError: (error) => toast({
      title: 'Подтягивание не выполнено',
      description: error instanceof Error ? error.message : 'unknown error',
      tone: 'danger',
    }),
  })
  const modelsQuery = useQuery({
    queryKey: ['agents', agent.name, 'models'],
    queryFn: () => fetchAgentModels(agent.name),
    enabled: editMode === 'model',
  })
  const enginesQuery = useQuery({
    queryKey: ['agents', agent.name, 'engines'],
    queryFn: () => fetchAgentEngines(agent.name),
    enabled: editMode === 'engine',
  })
  const stylesQuery = useQuery({
    queryKey: ['agents', agent.name, 'styles'],
    queryFn: () => fetchAgentStyles(agent.name),
    enabled: editMode === 'style',
  })
  const modelDiffMutation = useMutation({
    mutationFn: () => diffAgentModel(agent.name, model),
  })
  const modelConfirmMutation = useMutation({
    mutationFn: () => confirmAgentModel(agent.name, model),
    onSuccess: (result) => {
      toast({
        title: 'Модель записана',
        description: `${result.agentId}: MODEL=${result.model}; требуется restart агента.`,
        tone: 'success',
      })
      setEditMode(null)
      modelDiffMutation.reset()
      void queryClient.invalidateQueries({ queryKey: ['agents', 'health'] })
      void queryClient.invalidateQueries({ queryKey: ['agents', agent.name, 'models'] })
    },
    onError: (error) => toast({
      title: 'Модель не записана',
      description: error instanceof Error ? error.message : 'unknown error',
      tone: 'danger',
    }),
  })
  const engineDiffMutation = useMutation({
    mutationFn: () => diffAgentEngine(agent.name, engine, engineModel),
  })
  const engineConfirmMutation = useMutation({
    mutationFn: () => confirmAgentEngine(agent.name, engine, engineModel),
    onSuccess: (result) => {
      toast({
        title: 'Движок записан',
        description: `${result.agentId}: ENGINE=${result.engine}, model=${result.model}; требуется restart агента.`,
        tone: 'success',
      })
      setEditMode(null)
      engineDiffMutation.reset()
      void queryClient.invalidateQueries({ queryKey: ['agents', 'health'] })
      void queryClient.invalidateQueries({ queryKey: ['agents', agent.name, 'engines'] })
      void queryClient.invalidateQueries({ queryKey: ['agents', agent.name, 'models'] })
    },
    onError: (error) => toast({
      title: 'Движок не записан',
      description: error instanceof Error ? error.message : 'unknown error',
      tone: 'danger',
    }),
  })
  const styleDiffMutation = useMutation({
    mutationFn: () => diffAgentStyle(agent.name, style),
  })
  const styleConfirmMutation = useMutation({
    mutationFn: () => confirmAgentStyle(agent.name, style),
    onSuccess: (result) => {
      toast({
        title: 'Стиль записан',
        description: `${result.agentId}: AGENT_STYLE=${result.style}; требуется restart агента.`,
        tone: 'success',
      })
      setEditMode(null)
      styleDiffMutation.reset()
      void queryClient.invalidateQueries({ queryKey: ['agents', 'health'] })
      void queryClient.invalidateQueries({ queryKey: ['agents', agent.name, 'styles'] })
    },
    onError: (error) => toast({
      title: 'Стиль не записан',
      description: error instanceof Error ? error.message : 'unknown error',
      tone: 'danger',
    }),
  })

  function startModelEdit() {
    setModel(agent.model)
    setEditMode('model')
    modelDiffMutation.reset()
    engineDiffMutation.reset()
    styleDiffMutation.reset()
  }

  function startEngineEdit() {
    setEngine(agent.engine)
    setEngineModel(agent.model)
    setEditMode('engine')
    modelDiffMutation.reset()
    engineDiffMutation.reset()
    styleDiffMutation.reset()
  }

  function startStyleEdit() {
    setStyle(agent.style)
    setEditMode('style')
    modelDiffMutation.reset()
    engineDiffMutation.reset()
    styleDiffMutation.reset()
  }

  function cancelEdit() {
    setEditMode(null)
    modelDiffMutation.reset()
    engineDiffMutation.reset()
    styleDiffMutation.reset()
  }

  function changeEngine(nextEngine: string) {
    setEngine(nextEngine)
    const option = enginesQuery.data?.engines.find((item) => item.id === nextEngine)
    setEngineModel(option?.recommendedModel ?? '')
    engineDiffMutation.reset()
  }

  const selectedEngineOption = enginesQuery.data?.engines.find((item) => item.id === engine)
  const modelItems = modelsQuery.data?.modelItems ?? fallbackModelItems(agent.engine, modelsQuery.data?.models ?? [agent.model], model)
  const engineModelItems = selectedEngineOption?.modelItems ?? fallbackModelItems(engine, selectedEngineOption?.models ?? [engineModel || agent.model], engineModel)
  const agentModelLabel = modelLabelForUi(agent.engine, agent.model)
  const rows = [
    ['agentId', agent.name],
    ['роль', agent.role],
    ['mode', agent.mode],
    ['style', agent.style],
    ['model', agentModelLabel],
    ['движок', agent.engine],
    ['PID', String(agent.pid)],
    ['uptime', agent.uptime],
    ['проект', agent.project],
    ['спринт', agent.sprint],
    ['версия', `${agent.version.commit} ${agent.version.n ? `▲+${agent.version.n}` : ''}`.trim()],
    ['cwd', agent.cwd],
  ]
  return (
    <div className="agent-detail-overview" style={overviewGridStyle}>
      <WorkbenchPanel variant="raised">
        <div style={panelTitleRowStyle}>
          <div style={{ display: 'grid', gap: 3 }}>
            <div style={panelTitleFlushStyle}>Реквизиты</div>
            {editMode ? (
              <span style={{ color: 'var(--frm-text-3)', fontSize: 12 }}>
                {editMode === 'engine'
                  ? 'engine + model — запись в .env, restart отдельно'
                  : editMode === 'style'
                    ? 'style — запись в .env, restart отдельно'
                    : 'model — запись в .env, restart отдельно'}
              </span>
            ) : null}
          </div>
          {!editMode ? (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button size="sm" variant="ghost" style={outlineAccentButtonStyle} onClick={startEngineEdit}>
                Сменить engine
              </Button>
              <Button size="sm" variant="ghost" style={outlineAccentButtonStyle} onClick={startModelEdit}>
                Сменить model
              </Button>
              <Button size="sm" variant="ghost" style={outlineAccentButtonStyle} onClick={startStyleEdit}>
                Сменить style
              </Button>
            </div>
          ) : null}
        </div>
        <div className="agent-spec-table" style={specTableStyle}>
          {rows.map(([label, value]) => (
            <div key={label} style={{ ...specCellStyle, ...(label === 'cwd' ? fullWidthSpecCellStyle : null) }}>
              <span style={microLabelStyle}>{label}</span>
              {label === 'движок' && editMode === 'engine' ? (
                <Select
                  aria-label="ENGINE"
                  value={engine}
                  options={(enginesQuery.data?.engines ?? [{ id: agent.engine, label: agent.engine, models: [agent.model], recommendedModel: agent.model }]).map((item) => ({ value: item.id, label: item.label }))}
                  onChange={(event) => changeEngine(event.currentTarget.value)}
                />
              ) : label === 'model' && editMode === 'engine' ? (
                <Select
                  aria-label="Model for ENGINE"
                  value={engineModel}
                  options={(selectedEngineOption?.models ?? [engineModel || agent.model]).filter(Boolean).map((item) => ({
                    value: item,
                    label: optionLabelFromItems(engineModelItems, engine, item),
                  }))}
                  onChange={(event) => {
                    setEngineModel(event.currentTarget.value)
                    engineDiffMutation.reset()
                  }}
                />
              ) : label === 'model' && editMode === 'model' ? (
                <Select
                  aria-label="MODEL"
                  value={model}
                  options={(modelItems.length ? modelItems.map((item) => item.id) : [agent.model]).map((item) => ({
                    value: item,
                    label: optionLabelFromItems(modelItems, agent.engine, item),
                  }))}
                  onChange={(event) => {
                    setModel(event.currentTarget.value)
                    modelDiffMutation.reset()
                  }}
                />
              ) : label === 'style' && editMode === 'style' ? (
                <Select
                  aria-label="AGENT_STYLE"
                  value={style}
                  options={(stylesQuery.data?.styles ?? [{ id: agent.style, label: agent.style, description: '' }]).map((item) => ({ value: item.id, label: item.label }))}
                  onChange={(event) => {
                    setStyle(event.currentTarget.value)
                    styleDiffMutation.reset()
                  }}
                />
              ) : (
                <Mono style={label === 'model' || label === 'style' ? { color: 'var(--frm-accent)' } : undefined}>{value}</Mono>
              )}
            </div>
          ))}
        </div>
        {editMode === 'model' ? (
          <div style={editFooterStyle}>
            {modelsQuery.isError ? (
              <ErrorBanner
                title="Список моделей недоступен"
                description={modelsQuery.error instanceof Error ? modelsQuery.error.message : 'unknown error'}
              />
            ) : null}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <StatusPill status="ok" label="секреты scrubbed" />
              <StatusPill
                status={model !== agent.model ? 'changed' : 'unknown'}
                label={model !== agent.model ? 'есть изменения' : 'нет изменений'}
              />
              {modelDiffMutation.data ? (
                <StatusPill
                  status={modelDiffMutation.data.validation.ok ? 'ok' : 'danger'}
                  label={modelDiffMutation.data.validation.message}
                />
              ) : null}
            </div>
            <ModelCatalogList items={modelItems} current={model} onSelect={(next) => { setModel(next); modelDiffMutation.reset() }} />
            {modelDiffMutation.data ? (
              <DiffViewer
                compact
                lines={diffLinesFromText(modelDiffMutation.data.diff || 'Нет изменений.')}
                ariaLabel="Diff модели агента"
              />
            ) : null}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <Button loading={modelDiffMutation.isPending} onClick={() => modelDiffMutation.mutate()}>
                Сравнить изменения
              </Button>
              <Button
                variant="primary"
                loading={modelConfirmMutation.isPending}
                disabled={!modelDiffMutation.data?.canConfirm}
                onClick={() => modelConfirmMutation.mutate()}
              >
                Подтвердить запись
              </Button>
              <Button variant="ghost" onClick={cancelEdit}>
                Отмена
              </Button>
            </div>
            {modelDiffMutation.isError ? (
              <ErrorBanner
                title="Diff модели не построен"
                description={modelDiffMutation.error instanceof Error ? modelDiffMutation.error.message : 'unknown error'}
              />
            ) : null}
          </div>
        ) : editMode === 'style' ? (
          <div style={editFooterStyle}>
            {stylesQuery.isError ? (
              <ErrorBanner
                title="Список стилей недоступен"
                description={stylesQuery.error instanceof Error ? stylesQuery.error.message : 'unknown error'}
              />
            ) : null}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <StatusPill status="ok" label="секреты scrubbed" />
              <StatusPill
                status={style !== agent.style ? 'changed' : 'unknown'}
                label={style !== agent.style ? 'есть изменения' : 'нет изменений'}
              />
              {styleDiffMutation.data ? (
                <StatusPill
                  status={styleDiffMutation.data.validation.ok ? 'ok' : 'danger'}
                  label={styleDiffMutation.data.validation.message}
                />
              ) : null}
            </div>
            {styleDiffMutation.data ? (
              <DiffViewer
                compact
                lines={diffLinesFromText(styleDiffMutation.data.diff || 'Нет изменений.')}
                ariaLabel="Diff стиля агента"
              />
            ) : null}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <Button loading={styleDiffMutation.isPending} onClick={() => styleDiffMutation.mutate()}>
                Сравнить изменения
              </Button>
              <Button
                variant="primary"
                loading={styleConfirmMutation.isPending}
                disabled={!styleDiffMutation.data?.canConfirm}
                onClick={() => styleConfirmMutation.mutate()}
              >
                Подтвердить запись
              </Button>
              <Button variant="ghost" onClick={cancelEdit}>
                Отмена
              </Button>
            </div>
            {styleDiffMutation.isError ? (
              <ErrorBanner
                title="Diff стиля не построен"
                description={styleDiffMutation.error instanceof Error ? styleDiffMutation.error.message : 'unknown error'}
              />
            ) : null}
          </div>
        ) : editMode === 'engine' ? (
          <div style={editFooterStyle}>
            {enginesQuery.isError ? (
              <ErrorBanner
                title="Список движков недоступен"
                description={enginesQuery.error instanceof Error ? enginesQuery.error.message : 'unknown error'}
              />
            ) : null}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <StatusPill status="ok" label="адаптер подключён" />
              <StatusPill
                status={engine !== agent.engine || engineModel !== agent.model ? 'changed' : 'unknown'}
                label={engine !== agent.engine || engineModel !== agent.model ? 'есть изменения' : 'нет изменений'}
              />
              <StatusPill status="warning" label="restart required" />
              {engineDiffMutation.data ? (
                <StatusPill
                  status={engineDiffMutation.data.validation.ok ? 'ok' : 'danger'}
                  label={engineDiffMutation.data.validation.message}
                />
              ) : null}
            </div>
            <ModelCatalogList items={engineModelItems} current={engineModel} onSelect={(next) => { setEngineModel(next); engineDiffMutation.reset() }} />
            {engineDiffMutation.data ? (
              <DiffViewer
                compact
                lines={diffLinesFromText(engineDiffMutation.data.diff || 'Нет изменений.')}
                ariaLabel="Diff движка агента"
              />
            ) : null}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <Button loading={engineDiffMutation.isPending} onClick={() => engineDiffMutation.mutate()}>
                Сравнить изменения
              </Button>
              <Button
                variant="primary"
                loading={engineConfirmMutation.isPending}
                disabled={!engineDiffMutation.data?.canConfirm}
                onClick={() => engineConfirmMutation.mutate()}
              >
                Подтвердить запись
              </Button>
              <Button variant="ghost" onClick={cancelEdit}>
                Отмена
              </Button>
            </div>
            <span style={{ color: 'var(--frm-text-3)', fontSize: 12 }}>
              Запись не перезапускает systemd unit автоматически: примените restart отдельным действием после проверки diff.
            </span>
            {engineDiffMutation.isError ? (
              <ErrorBanner
                title="Diff движка не построен"
                description={engineDiffMutation.error instanceof Error ? engineDiffMutation.error.message : 'unknown error'}
              />
            ) : null}
          </div>
        ) : null}
      </WorkbenchPanel>
      <div style={{ display: 'grid', gap: 16 }}>
        <AgentSessionsWidget agentId={agent.name} />
        <WorkbenchPanel variant="raised">
          <div style={panelTitleStyle}>Версия → прод</div>
          <div style={{ display: 'grid', gap: 10 }}>
            <StatusPill
              status={agent.version.rel === 'up' ? 'ok' : agent.version.rel === 'behind' ? 'warning' : agent.version.rel === 'ahead' ? 'changed' : 'unknown'}
              label={agent.version.label}
              style={{ justifySelf: 'start' }}
            />
            <Mono>{agent.version.commit}</Mono>
            {agent.version.rel === 'ahead' ? (
              <Button
                loading={releaseMutation.isPending}
                style={{ ...outlineAccentButtonStyle, justifySelf: 'start' }}
                onClick={() => releaseMutation.mutate(agent.name)}
              >
                ▷ Выпустить в прод
              </Button>
            ) : agent.version.rel === 'behind' ? (
              <Button
                loading={pullMutation.isPending}
                style={{ ...outlineAccentButtonStyle, justifySelf: 'start' }}
                onClick={() => pullMutation.mutate(agent.name)}
              >
                ▷ Подтянуть
              </Button>
            ) : (
              <Button disabled style={{ justifySelf: 'start' }}>{agent.version.rel === 'up' ? 'Актуально' : 'Статус неизвестен'}</Button>
            )}
          </div>
        </WorkbenchPanel>
        {agent.error ? (
          <div style={errorPanelStyle}>
            <div style={errorTitleStyle}>Последняя ошибка</div>
            <div style={errorTextStyle}>{agent.error}</div>
          </div>
        ) : null}
      </div>
    </div>
  )
}

function ModelCatalogList({ items, current, onSelect }: { items: AgentModelItem[]; current: string; onSelect: (model: string) => void }) {
  if (!items.length) return null
  return (
    <div style={modelCatalogStyle}>
      {items.map((item) => {
        const selected = item.id === current
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => onSelect(item.id)}
            style={{
              ...modelCatalogItemStyle,
              ...(selected ? modelCatalogItemSelectedStyle : null),
            }}
          >
            <span style={modelCatalogTitleRowStyle}>
              <Mono style={{ color: selected ? 'var(--frm-accent)' : 'var(--frm-text)' }}>{item.label}</Mono>
              {item.isCurrent ? <StatusPill status="ok" label="current" /> : null}
              {item.isDefault ? <StatusPill status="unknown" label="default" /> : null}
            </span>
            {item.description ? <span style={modelCatalogDescriptionStyle}>{item.description}</span> : null}
          </button>
        )
      })}
    </div>
  )
}

function AgentSessionsWidget({ agentId }: { agentId: string }) {
  const sessionsQuery = useQuery({
    queryKey: ['sessions', 'agent', agentId],
    queryFn: fetchSessions,
    refetchInterval: 30_000,
  })
  const sessions = useMemo(
    () => (sessionsQuery.data ?? [])
      .filter((session) => session.agent === agentId && isActiveSession(session))
      .sort((a, b) => sessionSortWeight(b) - sessionSortWeight(a)),
    [agentId, sessionsQuery.data],
  )

  return (
    <WorkbenchPanel
      variant="raised"
      title="Активные сессии"
      description={sessionsQuery.data ? `${sessions.length} running/idle по agentId` : 'live /api/sessions'}
      actions={<Button size="sm" variant="ghost" loading={sessionsQuery.isFetching} onClick={() => void sessionsQuery.refetch()}>Обновить</Button>}
    >
      {sessionsQuery.isLoading ? (
        <Skeleton height={128} />
      ) : sessionsQuery.isError ? (
        <ErrorBanner
          title="Сессии недоступны"
          description={sessionsQuery.error instanceof Error ? sessionsQuery.error.message : 'unknown error'}
        />
      ) : sessions.length ? (
        <div style={agentSessionsListStyle}>
          {sessions.map((session) => (
            <AgentSessionRow key={session.id} session={session} />
          ))}
        </div>
      ) : (
        <div style={agentSessionsEmptyStyle}>Нет running/idle сессий этого агента</div>
      )}
    </WorkbenchPanel>
  )
}

function AgentSessionRow({ session }: { session: OwnerSessionDto }) {
  return (
    <div style={agentSessionRowStyle}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <StatusPill status={sessionStatusTone(session.status)} label={session.status} />
        <Pill>{session.channel}</Pill>
        <Mono>{formatAge(session.lastActivityAt)}</Mono>
      </div>
      <div style={{ display: 'grid', gap: 5, minWidth: 0 }}>
        <Mono style={{ color: 'var(--frm-text)' }}>{session.project || 'project unknown'}</Mono>
        <span style={{ color: 'var(--frm-text-3)', fontSize: 12 }}>
          {session.sprint || 'без спринта'} · {session.initiator}
        </span>
        {session.task ? <span style={agentSessionTaskStyle}>{session.task}</span> : null}
      </div>
    </div>
  )
}

function isActiveSession(session: OwnerSessionDto): boolean {
  return session.status === 'running' || session.status === 'idle'
}

function sessionStatusTone(status: SessionStatus): 'ok' | 'warning' | 'danger' | 'unknown' {
  if (status === 'running') return 'ok'
  if (status === 'idle') return 'warning'
  if (status === 'error') return 'danger'
  return 'unknown'
}

function sessionSortWeight(session: OwnerSessionDto): number {
  const statusWeight = session.status === 'running' ? 10_000_000_000_000 : 0
  const activity = Date.parse(session.lastActivityAt)
  return statusWeight + (Number.isFinite(activity) ? activity : 0)
}

function GroupTab({ agentId }: { agentId: string }) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const groupQuery = useQuery({
    queryKey: ['agents', agentId, 'group'],
    queryFn: () => fetchAgentGroup(agentId),
  })
  const [editMode, setEditMode] = useState(false)
  const [allowedChats, setAllowedChats] = useState('')
  const [groupProjects, setGroupProjects] = useState('')
  const [members, setMembers] = useState<MemberDraft[]>([])
  const input = useMemo<AgentGroupInput>(() => ({
    allowedGroupChatIds: splitLines(allowedChats),
    groupProjects: parseGroupProjects(groupProjects),
    allowedUserIds: members.map((member) => Number(member.id)).filter((item) => Number.isFinite(item)),
    ownerUserIds: members.filter((member) => member.owner).map((member) => Number(member.id)).filter((item) => Number.isFinite(item)),
  }), [allowedChats, groupProjects, members])
  const diffMutation = useMutation({
    mutationFn: () => diffAgentGroup(agentId, input),
  })
  const confirmMutation = useMutation({
    mutationFn: () => confirmAgentGroup(agentId, input),
    onSuccess: () => {
      toast({
        title: 'Группа записана',
        description: 'Конфиг env обновлён атомарно, требуется restart агента.',
        tone: 'success',
      })
      setEditMode(false)
      diffMutation.reset()
      void queryClient.invalidateQueries({ queryKey: ['agents', agentId, 'group'] })
    },
    onError: (error) => {
      toast({
        title: 'Запись не выполнена',
        description: error instanceof Error ? error.message : 'unknown error',
        tone: 'danger',
      })
    },
  })

  const config = groupQuery.data

  function startEdit() {
    if (!config) return
    setAllowedChats(config.allowedGroupChatIds.join('\n'))
    setGroupProjects(config.groupProjects.map((entry) => `${entry.chatId}=${entry.projects.join(',')}`).join('\n'))
    setMembers(mergeMembers(config.allowedUserIds, config.ownerUserIds))
    setEditMode(true)
    diffMutation.reset()
  }

  function cancelEdit() {
    setEditMode(false)
    diffMutation.reset()
  }

  function updateMember(index: number, patch: Partial<MemberDraft>) {
    setMembers((current) => current.map((member, idx) => (idx === index ? { ...member, ...patch } : member)))
    diffMutation.reset()
  }

  function removeMember(index: number) {
    setMembers((current) => current.filter((_member, idx) => idx !== index))
    diffMutation.reset()
  }

  if (groupQuery.isLoading) return <Skeleton height={240} />
  if (groupQuery.isError) {
    return (
      <ErrorBanner
        title="Группа недоступна"
        description={groupQuery.error instanceof Error ? groupQuery.error.message : 'unknown error'}
      />
    )
  }
  if (!config) return <EmptyState title="Нет group-конфига" />

  return (
    <WorkbenchPanel
      variant="raised"
      title="Группа · доступ"
      description={`${config.envFile} · после подтверждения нужен перезапуск`}
      actions={!editMode ? <Button style={outlineAccentButtonStyle} onClick={startEdit}>Редактировать</Button> : null}
    >
      {!editMode ? (
        <div className="agent-group-grid" style={groupGridStyle}>
          <div style={{ display: 'grid', gap: 14, minWidth: 0 }}>
            <ReadonlyBlock title="ALLOWED_GROUP_CHAT_IDS" lines={config.allowedGroupChatIds} />
            <ReadonlyBlock title="GROUP_PROJECTS" lines={config.groupProjects.map((entry) => `${entry.chatId}=${entry.projects.join(',')}`)} />
          </div>
          <div>
            <div style={fieldLabelStyle}>Участники</div>
            <div style={{ display: 'grid', gap: 8 }}>
              {mergeMembers(config.allowedUserIds, config.ownerUserIds).map((member) => (
                <MemberRow key={member.id} member={member} />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 14 }}>
          <div className="agent-group-grid" style={groupGridStyle}>
            <div style={{ display: 'grid', gap: 14, minWidth: 0 }}>
              <Textarea label="ALLOWED_GROUP_CHAT_IDS" rows={5} value={allowedChats} onChange={(event) => { setAllowedChats(event.currentTarget.value); diffMutation.reset() }} />
              <Textarea label="GROUP_PROJECTS" rows={5} value={groupProjects} onChange={(event) => { setGroupProjects(event.currentTarget.value); diffMutation.reset() }} />
            </div>
            <div>
              <div style={fieldLabelStyle}>Участники · owner даёт право на /release, /restart</div>
              <div style={{ display: 'grid', gap: 8 }}>
                {members.map((member, index) => (
                  <div key={`${member.id}-${index}`} style={memberEditRowStyle}>
                    <input
                      value={member.id}
                      aria-label={`user id ${index + 1}`}
                      onChange={(event) => updateMember(index, { id: event.currentTarget.value })}
                      style={memberInputStyle}
                    />
                    <button
                      type="button"
                      onClick={() => updateMember(index, { owner: !member.owner })}
                      style={member.owner ? memberOwnerActiveStyle : memberOwnerButtonStyle}
                    >
                      owner
                    </button>
                    <button type="button" onClick={() => removeMember(index)} style={removeMemberButtonStyle}>
                      −
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setMembers((current) => [...current, { id: '', owner: false }])
                    diffMutation.reset()
                  }}
                  style={addMemberButtonStyle}
                >
                  + Добавить участника
                </button>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Button loading={diffMutation.isPending} onClick={() => diffMutation.mutate()}>
              Сравнить изменения
            </Button>
            <Button variant="ghost" onClick={cancelEdit}>
              Отмена
            </Button>
          </div>
          {diffMutation.data && (
            <div style={{ display: 'grid', gap: 10 }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <StatusPill status={diffMutation.data.validation.ok ? 'ok' : 'danger'} label={diffMutation.data.validation.message} />
                <StatusPill status="ok" label="секреты scrubbed" />
                <StatusPill status={diffMutation.data.canConfirm ? 'ok' : 'unknown'} label={diffMutation.data.canConfirm ? 'confirm доступно' : 'нет изменений'} />
              </div>
              <DiffViewer compact lines={diffLinesFromText(diffMutation.data.diff || 'Нет изменений.')} ariaLabel="Diff группы агента" />
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <Button
                  variant="primary"
                  loading={confirmMutation.isPending}
                  disabled={!diffMutation.data?.canConfirm}
                  onClick={() => confirmMutation.mutate()}
                >
                  Подтвердить запись и перезапустить
                </Button>
                <Button variant="ghost" onClick={cancelEdit}>
                  Отмена
                </Button>
              </div>
            </div>
          )}
          {diffMutation.isError && (
            <ErrorBanner
              title="Diff не построен"
              description={diffMutation.error instanceof Error ? diffMutation.error.message : 'unknown error'}
            />
          )}
        </div>
      )}
    </WorkbenchPanel>
  )
}

function LogsTab({ agentId }: { agentId: string }) {
  const [search, setSearch] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const logsQuery = useQuery({
    queryKey: ['agents', agentId, 'logs', 50],
    queryFn: () => fetchAgentLogs(agentId, 50),
    refetchInterval: 30_000,
  })
  const lines = useMemo(() => filterLogLines(logsQuery.data?.text ?? '', search, from, to), [from, logsQuery.data?.text, search, to])
  const rangeSet = Boolean(from || to)

  return (
    <WorkbenchPanel variant="raised">
      <div style={logsHeaderStyle}>
        <div style={panelTitleFlushStyle}>Логи (scrubbed)</div>
        <span style={{ color: 'var(--frm-text-muted)', fontSize: 12 }}>последние 50 записей · автообновление</span>
      </div>
      <div style={logsToolbarStyle}>
        <SearchInput
          aria-label="Поиск по логам"
          controlSize="sm"
          fullWidth={false}
          inputStyle={{ fontFamily: 'var(--frm-font-mono)' }}
          onChange={(event) => setSearch(event.currentTarget.value)}
          placeholder="Поиск по логам"
          style={{ flex: '1 1 240px', minWidth: 220 }}
          value={search}
        />
        <div style={logRangeStyle}>
          <Input
            aria-label="Логи с даты"
            controlSize="sm"
            fullWidth={false}
            inputStyle={{ fontFamily: 'var(--frm-font-mono)' }}
            label="с"
            onChange={(event) => setFrom(event.currentTarget.value)}
            type="datetime-local"
            value={from}
          />
          <Input
            aria-label="Логи по дату"
            controlSize="sm"
            fullWidth={false}
            inputStyle={{ fontFamily: 'var(--frm-font-mono)' }}
            label="по"
            onChange={(event) => setTo(event.currentTarget.value)}
            type="datetime-local"
            value={to}
          />
          {rangeSet ? (
            <Button size="sm" variant="ghost" onClick={() => { setFrom(''); setTo('') }}>
              Сбросить
            </Button>
          ) : null}
          <Button size="sm" loading={logsQuery.isFetching} onClick={() => void logsQuery.refetch()}>
            Обновить
          </Button>
        </div>
      </div>
      {logsQuery.isLoading ? (
        <Skeleton height={300} />
      ) : logsQuery.isError ? (
        <ErrorBanner
          title="Логи недоступны"
          description={logsQuery.error instanceof Error ? logsQuery.error.message : 'unknown error'}
        />
      ) : (
        <div style={logStyle}>
          {lines.length ? (
            lines.map((line, index) => <LogLineRow key={`${index}-${line}`} line={line} />)
          ) : (
            <div style={logEmptyStyle}>Ничего не найдено по заданным фильтрам</div>
          )}
        </div>
      )}
    </WorkbenchPanel>
  )
}

function ReadonlyBlock({ title, lines }: { title: string; lines: string[] }) {
  return (
    <div style={readonlyStyle}>
      <div style={microLabelStyle}>{title}</div>
      {lines.length ? lines.map((line) => <Mono key={line}>{line}</Mono>) : <span style={{ color: 'var(--frm-text-3)' }}>empty</span>}
    </div>
  )
}

function MemberRow({ member }: { member: MemberDraft }) {
  return (
    <div style={memberRowStyle}>
      <Mono>{member.id}</Mono>
      <span style={member.owner ? memberOwnerBadgeStyle : memberUserBadgeStyle}>{member.owner ? 'owner' : 'user'}</span>
    </div>
  )
}

function LogLineRow({ line }: { line: string }) {
  const parts = splitLogLine(line)
  return (
    <div style={logLineStyle}>
      {parts.time ? <span style={logTimeStyle}>{parts.time}</span> : null}
      <span style={logMessageStyle}>{parts.message}</span>
    </div>
  )
}

function AgentStatus({ agent }: { agent: AgentHealth }) {
  const status = agent.state === 'active' ? 'ok' : agent.state === 'stale' ? 'warning' : 'danger'
  return <StatusPill status={status} label={agent.state} />
}

function LogIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M7 4h7l4 4v12a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" />
      <path d="M14 4v5h5" />
      <path d="M9 13h6M9 17h6" />
    </svg>
  )
}

function TelegramIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M21.94 4.6 18.6 20.3c-.25 1.1-.9 1.38-1.83.86l-5.05-3.72-2.44 2.35c-.27.27-.5.5-1.02.5l.36-5.14L17.98 6.5c.4-.36-.09-.56-.63-.2L6.5 13.04l-4.97-1.56c-1.08-.34-1.1-1.08.23-1.6L20.55 3c.9-.33 1.69.2 1.4 1.6Z" />
    </svg>
  )
}

function Mono({ children, style }: { children: React.ReactNode; style?: CSSProperties }) {
  return <span style={{ fontFamily: 'var(--frm-font-mono)', fontSize: 12.5, overflowWrap: 'anywhere', ...style }}>{children}</span>
}

function splitLines(value: string): string[] {
  return value.split(/\r?\n|,/).map((item) => item.trim()).filter(Boolean)
}

function parseGroupProjects(value: string) {
  return splitLines(value).map((line) => {
    const [chatId = '', projects = ''] = line.split('=')
    return { chatId: chatId.trim(), projects: projects.split(',').map((item) => item.trim()).filter(Boolean) }
  }).filter((entry) => entry.chatId)
}

function mergeMembers(allowedUserIds: number[], ownerUserIds: number[]): MemberDraft[] {
  const ownerSet = new Set(ownerUserIds.map(String))
  const ids = [...new Set([...allowedUserIds.map(String), ...ownerUserIds.map(String)])]
  return ids.map((id) => ({ id, owner: ownerSet.has(id) }))
}

function diffLinesFromText(text: string): DiffLine[] {
  const lines = text.split(/\r?\n/).filter((line) => line.length > 0)
  const source = lines.length ? lines : ['Нет изменений.']
  return source.map((line, index) => {
    const first = line[0]
    const kind = first === '+' ? 'added' : first === '-' ? 'removed' : first === '~' ? 'changed' : 'context'
    return {
      id: `${index}-${line}`,
      kind,
      content: kind === 'context' ? line : line.slice(1).trimStart(),
    }
  })
}

function filterLogLines(text: string, search: string, from: string, to: string): string[] {
  const needle = search.trim().toLowerCase()
  const fromMs = from ? Date.parse(from) : Number.NaN
  const toMs = to ? Date.parse(to) : Number.NaN
  return text
    .split(/\r?\n/)
    .map((line) => line.trimEnd())
    .filter(Boolean)
    .filter((line) => !needle || line.toLowerCase().includes(needle))
    .filter((line) => {
      if (!Number.isFinite(fromMs) && !Number.isFinite(toMs)) return true
      const lineMs = logLineTimeMs(line)
      if (!Number.isFinite(lineMs)) return true
      if (Number.isFinite(fromMs) && lineMs < fromMs) return false
      if (Number.isFinite(toMs) && lineMs > toMs) return false
      return true
    })
}

function logLineTimeMs(line: string): number {
  const iso = line.match(/\d{4}-\d{2}-\d{2}[T ][0-9:.]+(?:Z|[+-]\d{2}:?\d{2})?/)
  if (iso) return Date.parse(iso[0])
  const syslog = line.match(/^[A-Z][a-z]{2}\s+\d{1,2}\s+\d{2}:\d{2}:\d{2}/)
  if (syslog) return Date.parse(`${new Date().getFullYear()} ${syslog[0]}`)
  return Number.NaN
}

function splitLogLine(line: string): { time: string | null; message: string } {
  const iso = line.match(/^(\d{4}-\d{2}-\d{2}[T ][0-9:.]+(?:Z|[+-]\d{2}:?\d{2})?)\s+(.*)$/)
  if (iso) return { time: iso[1] ?? null, message: iso[2] ?? '' }
  const syslog = line.match(/^([A-Z][a-z]{2}\s+\d{1,2}\s+\d{2}:\d{2}:\d{2})\s+(.*)$/)
  if (syslog) return { time: syslog[1] ?? null, message: syslog[2] ?? '' }
  return { time: null, message: line }
}

const smallLinkStyle: CSSProperties = {
  color: 'var(--frm-text-3)',
  textDecoration: 'none',
  fontWeight: 600,
}

const agentDetailPageStyle: CSSProperties = {
  width: '100%',
  maxWidth: '100%',
  display: 'grid',
  gap: 18,
}

const heroStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 18,
  flexWrap: 'wrap',
  padding: 22,
  border: '1px solid var(--frm-line)',
  borderRadius: 18,
  background: 'linear-gradient(135deg, var(--frm-surface-raised), var(--frm-surface-2))',
  boxShadow: 'var(--frm-shadow-panel)',
}

const heroActionsStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 10,
  flexWrap: 'wrap',
}

const heroActionButtonStyle: CSSProperties = {
  minHeight: 38,
  borderColor: 'rgba(56,189,248,0.30)',
  background: 'rgba(56,189,248,0.10)',
  color: 'var(--frm-accent)',
  boxShadow: 'none',
}

const heroRestartButtonStyle: CSSProperties = {
  ...heroActionButtonStyle,
  borderColor: 'rgba(251,191,36,0.34)',
  background: 'rgba(251,191,36,0.10)',
  color: 'var(--frm-warning)',
}

const telegramPillStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  height: 24,
  padding: '0 10px',
  border: '1px solid rgba(56,189,248,0.34)',
  borderRadius: 999,
  background: 'rgba(56,189,248,0.10)',
  color: 'var(--frm-accent)',
  textDecoration: 'none',
  fontSize: 12,
  fontWeight: 800,
}

const overviewGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1.4fr) minmax(280px, 1fr)',
  gap: 16,
  alignItems: 'start',
}

const panelTitleRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
  marginBottom: 14,
  flexWrap: 'wrap',
}

const panelTitleStyle: CSSProperties = {
  marginBottom: 12,
  color: 'var(--frm-text)',
  fontSize: 16,
  fontWeight: 900,
}

const panelTitleFlushStyle: CSSProperties = {
  color: 'var(--frm-text)',
  fontSize: 16,
  fontWeight: 900,
}

const outlineAccentButtonStyle: CSSProperties = {
  borderColor: 'var(--frm-accent)',
  background: 'transparent',
  color: 'var(--frm-accent)',
  boxShadow: 'none',
}

const editFooterStyle: CSSProperties = {
  display: 'grid',
  gap: 12,
  marginTop: 14,
}

const modelCatalogStyle: CSSProperties = {
  display: 'grid',
  gap: 8,
}

const modelCatalogItemStyle: CSSProperties = {
  appearance: 'none',
  border: '1px solid var(--frm-border)',
  background: 'var(--frm-surface)',
  color: 'var(--frm-text)',
  borderRadius: 8,
  padding: '9px 11px',
  display: 'grid',
  gap: 5,
  textAlign: 'left',
  cursor: 'pointer',
}

const modelCatalogItemSelectedStyle: CSSProperties = {
  borderColor: 'var(--frm-accent)',
  boxShadow: '0 0 0 1px color-mix(in srgb, var(--frm-accent) 45%, transparent)',
}

const modelCatalogTitleRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
}

const modelCatalogDescriptionStyle: CSSProperties = {
  color: 'var(--frm-text-3)',
  fontSize: 12,
  lineHeight: 1.35,
}

const specTableStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 1,
  overflow: 'hidden',
  border: '1px solid var(--frm-line-subtle)',
  borderRadius: 11,
  background: 'var(--frm-line-subtle)',
}

const specCellStyle: CSSProperties = {
  display: 'grid',
  gap: 6,
  minHeight: 66,
  alignContent: 'center',
  padding: '11px 13px',
  background: 'var(--frm-workbench-well, #0B1020)',
  minWidth: 0,
}

const fullWidthSpecCellStyle: CSSProperties = {
  gridColumn: '1 / -1',
}

const microLabelStyle: CSSProperties = {
  color: 'var(--frm-text-3)',
  fontSize: 10.5,
  fontWeight: 850,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
}

const readonlyStyle: CSSProperties = {
  display: 'grid',
  gap: 8,
  alignContent: 'start',
  minHeight: 132,
  padding: 14,
  border: '1px solid var(--frm-line-subtle)',
  borderRadius: 12,
  background: 'var(--frm-workbench-well, #0B1020)',
}

const fieldLabelStyle: CSSProperties = {
  ...microLabelStyle,
  marginBottom: 8,
}

const groupGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 16,
  marginTop: 14,
  alignItems: 'start',
}

const memberRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 10,
  padding: '10px 13px',
  border: '1px solid var(--frm-line-subtle)',
  borderRadius: 10,
  background: 'var(--frm-workbench-panel-2, #161F35)',
}

const memberUserBadgeStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: 22,
  padding: '0 8px',
  borderRadius: 999,
  background: 'var(--frm-unknown-soft)',
  color: 'var(--frm-text-3)',
  fontSize: 11,
  fontWeight: 800,
}

const memberOwnerBadgeStyle: CSSProperties = {
  ...memberUserBadgeStyle,
  background: 'var(--frm-warning-soft)',
  color: 'var(--frm-warning)',
}

const memberEditRowStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto auto',
  alignItems: 'center',
  gap: 8,
}

const memberInputStyle: CSSProperties = {
  minWidth: 0,
  height: 38,
  padding: '0 11px',
  border: '1px solid var(--frm-line)',
  borderRadius: 9,
  background: 'var(--frm-workbench-well, #0B1020)',
  color: 'var(--frm-text)',
  fontFamily: 'var(--frm-font-mono)',
  fontSize: 12.5,
  outline: 0,
}

const memberOwnerButtonStyle: CSSProperties = {
  height: 38,
  padding: '0 10px',
  border: '1px solid var(--frm-line)',
  borderRadius: 9,
  background: 'transparent',
  color: 'var(--frm-text-3)',
  fontFamily: 'var(--frm-font-ui)',
  fontSize: 12,
  fontWeight: 800,
  cursor: 'pointer',
}

const memberOwnerActiveStyle: CSSProperties = {
  ...memberOwnerButtonStyle,
  borderColor: 'rgba(251,191,36,0.34)',
  background: 'rgba(251,191,36,0.10)',
  color: 'var(--frm-warning)',
}

const removeMemberButtonStyle: CSSProperties = {
  width: 38,
  height: 38,
  border: '1px solid rgba(251,113,133,0.32)',
  borderRadius: 9,
  background: 'rgba(251,113,133,0.08)',
  color: 'var(--frm-danger)',
  fontSize: 18,
  cursor: 'pointer',
}

const addMemberButtonStyle: CSSProperties = {
  height: 38,
  border: '1px dashed rgba(56,189,248,0.34)',
  borderRadius: 9,
  background: 'transparent',
  color: 'var(--frm-accent)',
  fontFamily: 'var(--frm-font-ui)',
  fontSize: 13,
  fontWeight: 800,
  cursor: 'pointer',
}

const logsHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  flexWrap: 'wrap',
  marginBottom: 14,
}

const logsToolbarStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flexWrap: 'wrap',
  marginBottom: 12,
}

const logRangeStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-end',
  gap: 8,
  flexWrap: 'wrap',
}

const logStyle: CSSProperties = {
  margin: 0,
  minHeight: 320,
  maxHeight: 440,
  overflow: 'auto',
  padding: '14px 16px',
  border: '1px solid var(--frm-line-subtle)',
  borderRadius: 11,
  background: 'var(--frm-workbench-well, #0B1020)',
  color: 'var(--frm-text-2)',
  fontFamily: 'var(--frm-font-mono)',
}

const logLineStyle: CSSProperties = {
  display: 'flex',
  gap: 12,
  minWidth: 0,
  fontSize: 12.5,
  lineHeight: 1.8,
  whiteSpace: 'pre-wrap',
}

const logTimeStyle: CSSProperties = {
  color: 'var(--frm-text-muted)',
  flexShrink: 0,
}

const logMessageStyle: CSSProperties = {
  minWidth: 0,
  color: 'var(--frm-text-2)',
}

const logEmptyStyle: CSSProperties = {
  minHeight: 260,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--frm-text-muted)',
  fontFamily: 'var(--frm-font-ui)',
  fontSize: 13,
}

const agentSessionsListStyle: CSSProperties = {
  display: 'grid',
  gap: 10,
  marginTop: 12,
}

const agentSessionRowStyle: CSSProperties = {
  display: 'grid',
  gap: 9,
  padding: '12px 13px',
  border: '1px solid var(--frm-line-subtle)',
  borderRadius: 12,
  background: 'var(--frm-workbench-well, #0B1020)',
  minWidth: 0,
}

const agentSessionsEmptyStyle: CSSProperties = {
  minHeight: 96,
  display: 'grid',
  placeItems: 'center',
  marginTop: 12,
  border: '1px dashed var(--frm-line)',
  borderRadius: 12,
  color: 'var(--frm-text-3)',
  fontSize: 13,
}

const agentSessionTaskStyle: CSSProperties = {
  color: 'var(--frm-text-2)',
  fontSize: 12.5,
  lineHeight: 1.5,
  overflowWrap: 'anywhere',
}

const errorPanelStyle: CSSProperties = {
  padding: '16px 18px',
  border: '1px solid rgba(251,113,133,0.28)',
  borderRadius: 14,
  background: 'linear-gradient(135deg, rgba(251,113,133,0.10), rgba(251,191,36,0.06))',
}

const errorTitleStyle: CSSProperties = {
  marginBottom: 8,
  color: 'var(--frm-danger)',
  fontSize: 11,
  fontWeight: 850,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
}

const errorTextStyle: CSSProperties = {
  color: '#FBA4B0',
  fontFamily: 'var(--frm-font-mono)',
  fontSize: 12.5,
  lineHeight: 1.6,
  overflowWrap: 'anywhere',
}
